/**
 * ASM-985: the test editor's lock session (HLD §7.2–§7.3, impl notes F3/F5).
 *
 * One session per tab per test lineage (`versionId ?? _id`). It is not tied to a React
 * component because it must survive SPA navigation between the test page and its question
 * and passage editors; `EditLockKeeper` feeds it the page's test, and leaving the test's pages
 * ends it.
 *
 * Doc-based tests and video quizzes take no lock: no session starts for them (the server
 * answers notLocked for them as well).
 *
 *   start ──► (shared & editing) acquire ──► held ──► renew every renewMs ──► … release
 *                                   │           └─► refused / sign-in changed ──► ended
 *                                   └─► notLocked / notConsulted / 404 ──► edit as today
 *
 * An ended session keeps sending its header marked `;ended`, so the server refuses its
 * guarded writes whatever the page still lets the user click.
 */
import uuid from 'uuid/v4'
import { testsApi } from '@edulastic/api'
import {
  setEditSessionProvider,
  setEditOutcomeListener,
  EDIT_SESSION_HEADER,
} from '@edulastic/api/src/utils/editSession'
import { getAccessToken, parseJwt } from '@edulastic/api/src/utils/Storage'
import { testCategoryTypes } from '@edulastic/constants/const/test'
import { getApiUri } from '../../../../utils/commons'
import { setEditLockStateAction, END_REASONS } from './ducks'

const DEFAULT_RENEW_MS = 2 * 60 * 1000
// a first acquire that got no answer is tried again after this, doubling up to renewMs
const ACQUIRE_RETRY_MS = 5 * 1000

let dispatch = () => {}
let s = null

const tokenSessionId = () => {
  const token = getAccessToken()
  if (!token) return null
  try {
    return parseJwt(token)?.sessionId || null
  } catch (e) {
    return null
  }
}

const publish = () => {
  dispatch(
    setEditLockStateAction(
      s
        ? {
            active: true,
            testId: s.testId,
            editing: s.editing,
            acquireState: s.acquire,
            required: s.required,
            heldByThisTab: s.held,
            heldByOther: s.heldByOther,
            holderName: s.holderName,
            ended: s.ended,
            endReason: s.endReason,
            activeTestId: s.activeTestId,
          }
        : { active: false }
    )
  )
}

const headerFor = (session) => {
  const parts = [session.id]
  if (typeof session.version === 'number') parts.push(`v=${session.version}`)
  if (session.editing) parts.push('edit')
  if (session.ended) parts.push('ended')
  return parts.join(';')
}

export const headerValue = () => (s ? headerFor(s) : null)

const stopTimer = () => {
  if (s?.timer) clearTimeout(s.timer)
  if (s) s.timer = null
}

const scheduleRenewal = () => {
  stopTimer()
  if (!s || s.ended || !s.held) return
  const session = s
  // renewNow schedules the next tick through here: mutual recursion
  // eslint-disable-next-line no-use-before-define
  s.timer = setTimeout(() => renewNow(session), s.renewMs)
}

const sendRelease = (session) => {
  // goes out with the session's own header (marked ended when it ended), so only this
  // tab's entry is dropped; the teacher's other tabs keep the lock. Passed explicitly:
  // clearSession has already forgotten the session, so the provider has no header for it
  if (!session?.testId) return Promise.resolve()
  return testsApi
    .releaseEditLock(session.testId, headerFor(session))
    .catch(() => {})
}

/** Ending a lock session, for any reason, always sends the release (F5). Idempotent. */
export const endSession = (reason, { holderName, activeTestId } = {}) => {
  if (!s || s.ended) return
  stopTimer()
  s.ended = true
  s.endReason = reason
  s.held = false
  if (holderName) {
    s.heldByOther = true
    s.holderName = holderName
  }
  if (activeTestId) s.activeTestId = activeTestId
  publish()
  sendRelease(s)
}

/** Leaving the test's pages, or starting another lineage: release if held, then forget it. */
export const clearSession = () => {
  if (!s) return
  const session = s
  stopTimer()
  s = null
  publish()
  if (session.held && !session.ended) sendRelease({ ...session })
}

const begin = (test) => {
  s = {
    id: uuid(),
    lineage: `${test.versionId || test._id}`,
    testId: test._id,
    version: typeof test.version === 'number' ? test.version : undefined,
    seenEntityVersion: test.version,
    editing: false,
    // set by Edit (acquireNow) until publish: the page's own flag flips a moment later
    editPinned: false,
    // set by publish until the page stops reporting editing: its status flips a moment later
    releasedHold: false,
    ended: false,
    endReason: null,
    // `required` starts from the authors and is then set only by the lock's answers (F1)
    required: (test.authors || []).length > 1,
    held: false,
    heldByOther: false,
    holderName: '',
    activeTestId: null,
    lockKey: null,
    renewMs: DEFAULT_RENEW_MS,
    retryMs: ACQUIRE_RETRY_MS,
    acquire: 'none', // none | pending | retry | done
    rechecking: false,
    tokenSessionId: tokenSessionId(),
    timer: null,
  }
}

const markHeld = (lockKey) => {
  s.held = true
  s.required = true
  s.heldByOther = false
  s.holderName = ''
  if (lockKey) s.lockKey = lockKey
  // a save may take the lock while a retry is waiting: the renewal timer replaces it
  if (s.acquire === 'retry') {
    s.acquire = 'done'
    stopTimer()
  }
  s.retryMs = ACQUIRE_RETRY_MS
  if (!s.timer) scheduleRenewal()
}

const answerAcquire = (result) => {
  if (!s || s.ended) return { ok: false }
  if (result?.held) {
    if (result.renewMs) s.renewMs = result.renewMs
    markHeld(result.lockKey)
    publish()
    return { ok: true }
  }
  // notLocked (single author), notConsulted (Redis down, kill switch): edit as today
  s.required = false
  s.held = false
  stopTimer()
  publish()
  return { ok: true }
}

const failAcquire = (err) => {
  if (!s || s.ended) return { ok: false }
  const status = err?.status
  const code = err?.response?.data?.code
  if (status === 409 && code) {
    // the outcome listener has already ended the session with the right message
    return { ok: false }
  }
  if (status === 403) {
    endSession(END_REASONS.ACCESS)
    return { ok: false }
  }
  if (status === 404) {
    // an old backend: no lock to take, edit as today (B4a)
    s.required = false
    publish()
    return { ok: true }
  }
  if (status && status >= 400 && status < 500 && status !== 429) {
    endSession(END_REASONS.ERROR)
    return { ok: false }
  }
  // no answer (network, 5xx, 429): keep the session; the caller tries again on a timer
  return { ok: true, retry: true }
}

/**
 * The first acquire got no answer: the tab is blocked until it holds the lock, so try again
 * on a timer (no renewal runs, as the tab holds nothing). Stops once the tab is not editing;
 * maybeAcquire starts over if it edits again.
 */
const scheduleAcquireRetry = () => {
  if (!s || s.ended || s.held) return
  stopTimer()
  const session = s
  const wait = s.retryMs
  s.retryMs = Math.min(wait * 2, s.renewMs)
  s.acquire = 'retry'
  s.timer = setTimeout(() => {
    if (session !== s || s.ended || s.held) return
    s.timer = null
    if (!s.editing) {
      s.acquire = 'none'
      publish()
      return
    }
    // eslint-disable-next-line no-use-before-define
    acquireNow({ editing: true })
  }, wait)
}

/** Take the lock now (load of a shared draft, or Edit on a published test). */
export const acquireNow = async ({ editing = true } = {}) => {
  if (!s || s.ended) return { ok: !s }
  const session = s
  // a waiting retry is this call; a renewal timer stays
  if (s.acquire === 'retry') stopTimer()
  s.editing = editing
  s.editPinned = editing
  s.releasedHold = false
  s.acquire = 'pending'
  publish()
  try {
    const result = await testsApi.acquireEditLock(session.testId)
    if (session !== s || session.ended) {
      // the tab left the test, started another, or ended this session while the answer
      // was on its way: a release sent then could not drop a hold granted after it, and
      // nothing else would; the answer belongs to no current session
      session.acquire = 'done'
      if (result?.held) sendRelease({ ...session })
      return { ok: false }
    }
    s.acquire = 'done'
    return answerAcquire(result)
  } catch (err) {
    if (session !== s) {
      session.acquire = 'done'
      return { ok: false }
    }
    s.acquire = 'done'
    const answer = failAcquire(err)
    if (answer.retry && session === s) scheduleAcquireRetry()
    // the refusal may have published before `acquire` was done; publish the final state
    publish()
    return answer
  }
}

const maybeAcquire = () => {
  if (!s || s.ended || !s.editing || !s.required) return
  if (s.acquire !== 'none') return
  acquireNow({ editing: true })
}

/**
 * The page's test and whether it is editing. Called on every render of a test-editing page;
 * cheap when nothing changed.
 */
const isLockExempt = (test) =>
  !!test.isDocBased ||
  test.testCategory === testCategoryTypes.DOC_BASED ||
  test.testCategory === testCategoryTypes.VIDEO_BASED

export const syncTest = ({ test, editing, attach }) => {
  if (attach) dispatch = attach
  if (!test?._id) return
  if (isLockExempt(test)) {
    clearSession()
    return
  }
  const lineage = `${test.versionId || test._id}`
  if (s && s.lineage !== lineage) clearSession()
  if (!s) {
    begin(test)
    s.editing = !!editing
    publish()
    maybeAcquire()
    return
  }
  let changed = false
  if (s.testId !== test._id) {
    // a fork's new id, or Discard back to the previous one: same lineage, same session
    s.testId = test._id
    changed = true
  }
  if (test.version !== s.seenEntityVersion) {
    // the page loaded (or saved) the test: that version is what this tab now holds.
    // Local edits keep the entity's version, so they never move it backwards.
    s.seenEntityVersion = test.version
    if (typeof test.version === 'number') s.version = test.version
  }
  // a render between publish (or Discard) and the page's own update still says editing: a
  // re-take there would hold the lock just freed
  if (!editing) s.releasedHold = false
  const wantEditing = (!!editing && !s.releasedHold) || s.editPinned
  if (!s.ended && s.editing !== wantEditing) {
    s.editing = wantEditing
    changed = true
  }
  if (changed) publish()
  maybeAcquire()
}

/** One renewal tick; also run when the tab becomes visible or comes back online. */
export const renewNow = async (session = s) => {
  if (!session || session !== s || s.ended || !s.held) return
  const sid = tokenSessionId()
  if (!sid || sid !== s.tokenSessionId) {
    endSession(END_REASONS.SIGN_IN)
    return
  }
  let state
  try {
    const result = await testsApi.renewEditLock(s.testId, s.lockKey)
    state = result?.state
  } catch (err) {
    if (session !== s || s.ended) return
    if (err?.status === 404) {
      // the backend was rolled back under an open editor: stop and edit as today
      stopTimer()
      s.held = false
      s.required = false
      publish()
      return
    }
    scheduleRenewal()
    return
  }
  if (session !== s || s.ended) return
  if (!state || state === 'renewed' || state === 'notConsulted') {
    // a null result is no answer (the 401 path resolves with result null): keep going
    scheduleRenewal()
    return
  }
  if (!s.editing) {
    // lost while viewing (Discard released it, or the ceiling): a viewer does not re-take;
    // EDIT acquires again
    stopTimer()
    s.held = false
    s.acquire = 'none'
    publish()
    return
  }
  // free, ceiling, or another teacher: re-take from this session; its answer decides
  try {
    const result = await testsApi.acquireEditLock(s.testId)
    if (session !== s || s.ended) return
    if (result?.held) {
      markHeld(result.lockKey)
      scheduleRenewal()
    } else if (result?.notLocked) {
      // the holder unshared the last co-author: single author now, keep editing
      stopTimer()
      s.held = false
      s.required = false
      sendRelease(s)
    } else {
      scheduleRenewal()
    }
    publish()
  } catch (err) {
    if (session !== s || s.ended) return
    const r = failAcquire(err)
    if (r.retry) scheduleRenewal()
  }
}

/**
 * A write refused for permission while this tab holds the lock: edit access may have been
 * removed (unsharing leaves the lock in place). The lock's own full check decides; its 403
 * ends the session, which releases the lock.
 */
const recheckAccess = async () => {
  const session = s
  session.rechecking = true
  try {
    const result = await testsApi.acquireEditLock(session.testId)
    if (session === s && !s.ended) answerAcquire(result)
  } catch (err) {
    if (session === s && !s.ended) {
      failAcquire(err)
      publish()
    }
  } finally {
    session.rechecking = false
  }
}

/** Every response that carried this tab's header (via the API client's listener). */
const onOutcome = (info) => {
  if (!s) return
  // a response to a request another session sent (an answer that outlived its session)
  if (info.session && info.session !== s.id) return
  if (info.status === 403) {
    // the lock's own requests handle their 403 themselves
    if (/\/edit-lock/.test(info.url || '')) return
    if (s.held && !s.ended && !s.rechecking) recheckAccess()
    return
  }
  if (info.status === 409) {
    if (s.ended) return
    if (info.code === 'EDIT_LOCK_HELD') {
      endSession(END_REASONS.HELD, { holderName: info.holder?.name || '' })
    } else if (info.code === 'EDIT_LOCK_STALE' || info.code === 'TEST_STALE') {
      endSession(END_REASONS.STALE, { activeTestId: info.activeTestId })
    }
    return
  }
  let changed = false
  const v = Number(info.testVersion)
  if (info.testVersion !== undefined && !Number.isNaN(v)) {
    s.version = v
  }
  if (info.lock && info.lock.startsWith('held') && !s.ended) {
    const key = (info.lock.match(/key=([0-9a-fA-F]{24})/) || [])[1]
    if (!s.held) changed = true
    markHeld(key)
  }
  if (changed) publish()
}

// the server freed the lock: this tab is now viewing. Renders that still say editing (before
// the page's own state catches up) must not re-take it, so hold until one says viewing.
const stopEditing = () => {
  if (!s) return
  stopTimer()
  s.held = false
  s.editing = false
  s.editPinned = false
  s.releasedHold = true
  s.acquire = 'none'
  publish()
}

/** Publish succeeded: the server freed the lock; this tab is now viewing. */
export const onPublished = () => stopEditing()

/** Discard (rollback to the previous version) succeeded: the server freed the lock. */
export const onDiscarded = () => stopEditing()

export const getSession = () => s

// the test's own pages: the test page (by id, its tabs, create, by version), and its question
// and passage editors (/author/tests/<id>/…, /author/items/<id>/item-detail/test/<id>). Not
// the list, and not the publish / assign success pages.
export const isTestEditingPath = (pathname = '') =>
  (/^\/author\/tests\/(tab\/|create|verid\/|[0-9a-fA-F]{24})/.test(pathname) &&
    !/^\/author\/tests\/[0-9a-fA-F]{24}\/(publish|assign\/|async-assign)/.test(
      pathname
    )) ||
  /^\/author\/items\/[0-9a-fA-F]{24}\/item-detail\/test\/[0-9a-fA-F]{24}/.test(
    pathname
  )

/** Wire the session into the app once (called from the author app's start). */
export const installEditLockSession = ({ history, store }) => {
  if (store) dispatch = store.dispatch
  setEditSessionProvider(headerValue)
  setEditOutcomeListener(onOutcome)
  if (history?.listen) {
    history.listen((location) => {
      if (s && !isTestEditingPath(location?.pathname)) clearSession()
    })
  }
  window.addEventListener('user-token-expired', () =>
    endSession(END_REASONS.SIGN_IN)
  )
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') renewNow()
  })
  window.addEventListener('online', () => renewNow())
  window.addEventListener('pagehide', () => {
    // only a tab that holds the lock, or may be granted it by an acquire still in flight
    if (!s || !s.testId || !(s.held || s.acquire === 'pending')) return
    // fetch with keepalive: sendBeacon cannot send the Authorization header
    try {
      fetch(`${getApiUri().replace(/\/$/, '')}/test/${s.testId}/edit-lock`, {
        method: 'DELETE',
        keepalive: true,
        headers: {
          Authorization: getAccessToken() || '',
          [EDIT_SESSION_HEADER]: headerValue(),
        },
      })
    } catch (e) {
      // the lease expires on its own
    }
  })
}
